/**
 * Deterministic replacements for Math.sin/cos/atan2/hypot.
 *
 * ECMAScript leaves those "implementation-approximated", and engines really do differ
 * (measured 2026-10-03: Chromium, Firefox and WebKit disagree on all of them, so a
 * match diverges within 500 ticks). Only + - * / and Math.sqrt are exactly rounded
 * everywhere, so everything here is built from those plus exact helpers (Math.round,
 * Math.abs, %). Algorithms and coefficients: FreeBSD msun (fdlibm) k_sin.c, k_cos.c,
 * e_rem_pio2.c, s_atan.c, e_atan2.c, with bit tests replaced by value comparisons.
 * Accuracy is within ~1 ulp of the libm results; the point is identical bits on every
 * engine, which tests/e2e/determinism.spec.ts checks.
 */

const PI = Math.PI;
const PI_LO = 1.2246467991473532e-16;
const HALF_PI = Math.PI / 2;
const QUARTER_PI = Math.PI / 4;

// sin/cos kernels on [-pi/4, pi/4] (k_sin.c, k_cos.c).
const S1 = -0.16666666666666632;
const S2 = 0.00833333333332249;
const S3 = -0.0001984126982985795;
const S4 = 0.0000027557313707070068;
const S5 = -2.5050760253406863e-8;
const S6 = 1.58969099521155e-10;
const C1 = 0.0416666666666666;
const C2 = -0.001388888888887411;
const C3 = 0.00002480158728947673;
const C4 = -2.7557314351390663e-7;
const C5 = 2.087572321298175e-9;
const C6 = -1.1359647557788195e-11;

/** sin(x + y) for |x| <= pi/4, where y is the tail of the reduced argument. */
function kernelSin(x: number, y: number): number {
	const z = x * x;
	const w = z * z;
	const r = S2 + z * (S3 + z * S4) + z * w * (S5 + z * S6);
	const v = z * x;
	if (y === 0) return x + v * (S1 + z * r);
	return x - (z * (0.5 * y - v * r) - y - v * S1);
}

function kernelCos(x: number, y: number): number {
	const z = x * x;
	let w = z * z;
	const r = z * (C1 + z * (C2 + z * C3)) + w * w * (C4 + z * (C5 + z * C6));
	const hz = 0.5 * z;
	w = 1 - hz;
	return w + (1 - w - hz + (z * r - x * y));
}

// Cody-Waite reduction by pi/2 in three 33-bit parts (e_rem_pio2.c, medium case).
const INV_PIO2 = 0.6366197723675814;
const PIO2_1 = 1.5707963267341256;
const PIO2_2 = 6.077100506303966e-11;
const PIO2_2T = 2.0222662487959506e-21;
const PIO2_3 = 2.0222662487111665e-21;
const PIO2_3T = 8.4784276603689e-32;
/** Beyond this the three-part reduction loses precision; pre-reduce with an exact `%`. */
const MEDIUM_LIMIT = 4e5;
const TAU = 2 * Math.PI;

// Reduction output, reused to avoid allocating in hot loops.
let reducedHi = 0;
let reducedLo = 0;

/** Sets reducedHi + reducedLo = x - n*pi/2 and returns n (mod 4). */
function reduce(x: number): number {
	const v = Math.abs(x) > MEDIUM_LIMIT ? x % TAU : x;
	const fn = Math.round(v * INV_PIO2);
	let t = v - fn * PIO2_1;
	let w = fn * PIO2_2;
	let r = t - w;
	w = fn * PIO2_2T - (t - r - w);
	t = r;
	w = fn * PIO2_3;
	r = t - w;
	w = fn * PIO2_3T - (t - r - w);
	reducedHi = r - w;
	reducedLo = r - reducedHi - w;
	return ((fn % 4) + 4) % 4;
}

export function sin(x: number): number {
	if (x === 0) return x; // keeps -0
	if (Math.abs(x) <= QUARTER_PI) return kernelSin(x, 0);
	if (!Number.isFinite(x)) return Number.NaN;
	switch (reduce(x)) {
		case 0:
			return kernelSin(reducedHi, reducedLo);
		case 1:
			return kernelCos(reducedHi, reducedLo);
		case 2:
			return -kernelSin(reducedHi, reducedLo);
		default:
			return -kernelCos(reducedHi, reducedLo);
	}
}

export function cos(x: number): number {
	if (Math.abs(x) <= QUARTER_PI) return kernelCos(x, 0);
	if (!Number.isFinite(x)) return Number.NaN;
	switch (reduce(x)) {
		case 0:
			return kernelCos(reducedHi, reducedLo);
		case 1:
			return -kernelSin(reducedHi, reducedLo);
		case 2:
			return -kernelCos(reducedHi, reducedLo);
		default:
			return kernelSin(reducedHi, reducedLo);
	}
}

// s_atan.c
const ATAN_HI = [
	0.4636476090008061, 0.7853981633974483, 0.982793723247329, 1.5707963267948966,
] as const;
const ATAN_LO = [
	2.2698777452961687e-17, 3.061616997868383e-17, 1.3903311031230998e-17, 6.123233995736766e-17,
] as const;
const AT0 = 0.3333333333333293;
const AT1 = -0.19999999999876483;
const AT2 = 0.14285714272503466;
const AT3 = -0.11111110405462356;
const AT4 = 0.09090887133436507;
const AT5 = -0.0769187620504483;
const AT6 = 0.06661073137387531;
const AT7 = -0.058335701337905735;
const AT8 = 0.049768779946159324;
const AT9 = -0.036531572744216916;
const AT10 = 0.016285820115365782;

export function atan(x: number): number {
	if (Number.isNaN(x)) return x;
	let a = Math.abs(x);
	if (a >= 7.378697629483821e19) return x < 0 ? -HALF_PI : HALF_PI; // 2^66
	let id = -1;
	if (a >= 0.4375) {
		if (a < 1.1875) {
			if (a < 0.6875) {
				id = 0;
				a = (2 * a - 1) / (2 + a);
			} else {
				id = 1;
				a = (a - 1) / (a + 1);
			}
		} else if (a < 2.4375) {
			id = 2;
			a = (a - 1.5) / (1 + 1.5 * a);
		} else {
			id = 3;
			a = -1 / a;
		}
	} else {
		a = x;
	}
	const z = a * a;
	const w = z * z;
	const s1 = z * (AT0 + w * (AT2 + w * (AT4 + w * (AT6 + w * (AT8 + w * AT10)))));
	const s2 = w * (AT1 + w * (AT3 + w * (AT5 + w * (AT7 + w * AT9))));
	if (id < 0) return a - a * (s1 + s2);
	const r = (ATAN_HI[id] as number) - (a * (s1 + s2) - (ATAN_LO[id] as number) - a);
	return x < 0 ? -r : r;
}

/** Same special cases as Math.atan2; atan2(0, 0) = 0 (legacy relies on it). */
export function atan2(y: number, x: number): number {
	if (Number.isNaN(x) || Number.isNaN(y)) return Number.NaN;
	const yNeg = y < 0 || Object.is(y, -0);
	const xNeg = x < 0 || Object.is(x, -0);
	if (y === 0) return xNeg ? (yNeg ? -PI : PI) : y;
	if (x === 0) return yNeg ? -HALF_PI : HALF_PI;
	if (!Number.isFinite(x)) {
		if (!Number.isFinite(y)) {
			const r = xNeg ? 3 * QUARTER_PI : QUARTER_PI;
			return yNeg ? -r : r;
		}
		const r = xNeg ? PI : 0;
		return yNeg ? -r : r;
	}
	if (!Number.isFinite(y)) return yNeg ? -HALF_PI : HALF_PI;
	const z = atan(Math.abs(y / x));
	if (!xNeg) return yNeg ? -z : z;
	return yNeg ? z - PI_LO - PI : PI - (z - PI_LO);
}

/** Two-argument hypot. Sim values are far from overflow, so no scaling is needed. */
export function hypot(x: number, y: number): number {
	return Math.sqrt(x * x + y * y);
}
